import React from "react";
import styled, { keyframes } from "styled-components";

const logos = [
  {
    name: "Google",
    src: "https://raw.githubusercontent.com/simple-icons/simple-icons/develop/icons/google.svg",
    isMonochrome: false
  },
  {
    name: "Fetch.ai",
    src: "https://raw.githubusercontent.com/cosmos/chain-registry/master/fetchhub/images/fet.svg",
    isMonochrome: false
  },
  {
    name: "Vodafone",
    src: "https://raw.githubusercontent.com/simple-icons/simple-icons/develop/icons/vodafone.svg",
    isMonochrome: false
  },
  {
    name: "EE",
    src: "https://upload.wikimedia.org/wikipedia/commons/2/2f/EE_logo.svg",
    isMonochrome: false
  },
  {
    name: "UK Government",
    src: "https://www.vectorlogo.zone/logos/govuk/govuk-ar21.svg",
    isMonochrome: true
  },
  {
    name: "Accenture",
    src: "https://raw.githubusercontent.com/simple-icons/simple-icons/develop/icons/accenture.svg",
    isMonochrome: true
  }
];

const scroll = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const SectionContainer = styled.div`
  margin: 60px 0 100px;
  width: 100%;
`;

const SectionHeader = styled.div`
  max-width: 1400px;
  margin: 0 auto 30px;
  padding: 0 40px;
  
  @media (max-width: 768px) {
    padding: 0 20px;
  }

  h2 {
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: var(--text-color);
    opacity: 0.4;
    margin: 0;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: 20px 0;
  border-top: 1px solid rgba(150, 150, 150, 0.1);
  border-bottom: 1px solid rgba(150, 150, 150, 0.1);

  /* Linear gradient masking for seamless fade edges */
  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    width: 200px;
    height: 100%;
    z-index: 2;
    pointer-events: none;
    
    @media (max-width: 768px) {
      width: 80px;
    }
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, var(--bg-color), transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, var(--bg-color), transparent);
  }
`;

const CarouselTrack = styled.div`
  display: flex;
  width: max-content;
  align-items: center;
  animation: ${scroll} 25s linear infinite;

  &:hover {
    animation-play-state: paused;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 25px;
  height: 45px;
  cursor: pointer;
  
  /* Apply opacity to the whole wrapper so text and logo fade in unison */
  opacity: 0.4;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  [data-theme="dark"] & {
    opacity: 0.4;
  }

  &:hover {
    opacity: 1;
  }
  
  @media (max-width: 768px) {
    margin: 0 15px;
    height: 35px;
    gap: 8px;
  }
`;

const LogoImg = styled.img`
  height: 100%;
  width: auto;
  max-height: 32px;
  object-fit: contain;
  transition: all 0.3s ease;

  /* Default grayscale */
  filter: grayscale(100%) contrast(0.8);

  [data-theme="dark"] & {
    /* If monochrome, invert to white in dark mode. Otherwise keep grayscale */
    filter: ${props =>
      props.isMonochrome
        ? "grayscale(100%) invert(1)"
        : "grayscale(100%) contrast(0.8)"};
  }

  /* When the parent wrapper is hovered, restore original colored state */
  ${LogoWrapper}:hover & {
    filter: grayscale(0%) contrast(1);

    [data-theme="dark"] & {
      filter: ${props =>
        props.isMonochrome
          ? "grayscale(100%) invert(1)"
          : "grayscale(0%) contrast(1)"};
    }
  }
  
  @media (max-width: 768px) {
    max-height: 24px;
  }
`;

const CompanyName = styled.span`
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--text-color);
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    letter-spacing: 1px;
  }
`;

const LogoCarousel = () => {
  // Duplicate logos array once to create the infinite scrolling loop
  const displayLogos = [...logos, ...logos];

  return (
    <SectionContainer>
      <SectionHeader>
        <h2>trusted by & collaborated with</h2>
      </SectionHeader>
      <CarouselContainer>
        <CarouselTrack>
          {displayLogos.map((logo, index) => (
            <LogoWrapper key={index} title={logo.name}>
              <LogoImg
                src={logo.src}
                alt={logo.name}
                isMonochrome={logo.isMonochrome}
              />
              <CompanyName>{logo.name}</CompanyName>
            </LogoWrapper>
          ))}
        </CarouselTrack>
      </CarouselContainer>
    </SectionContainer>
  );
};

export default LogoCarousel;
